import React from "react";
import {
  TextField,
  Button,
  Typography,
  InputAdornment,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  Checkbox,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IOSSwitch from "../../../components/IOSSwitch";

interface Props {
  isAwsScan: boolean;
  /** When true render standard labels (not centered placeholders) */
  useLabels?: boolean;
  newCredUsername: string;
  setNewCredUsername: (v: string) => void;
  newCredSecret: string;
  setNewCredSecret: (v: string) => void;
  newCredShowPassword: boolean;
  setNewCredShowPassword: (b: boolean) => void;
  newCredPasswordType: "password" | "private_key";
  setNewCredPasswordType: (v: "password" | "private_key") => void;
  newCredPrivateKeyFileName: string;
  newCredPrivateKeyError: string;
  ppkInputRef: React.RefObject<HTMLInputElement | null>;
  handlePpkUploadClick: () => void;
  handlePpkFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  newCredExecPasswordEnabled: boolean;
  setNewCredExecPasswordEnabled: (b: boolean) => void;
  newCredShowExecPassword: boolean;
  setNewCredShowExecPassword: (b: boolean) => void;
  newCredExecPasswordText: string;
  setNewCredExecPasswordText: (s: string) => void;
  newCredAutoAssign: boolean;
  setNewCredAutoAssign: (b: boolean) => void;
  newCredSave: boolean;
  setNewCredSave: (b: boolean) => void;
  newCredName: string;
  setNewCredName: (s: string) => void;
  fieldSx: any;
}

const CredentialCreator: React.FC<Props> = ({
  isAwsScan,
  useLabels,
  newCredUsername,
  setNewCredUsername,
  newCredSecret,
  setNewCredSecret,
  newCredShowPassword,
  setNewCredShowPassword,
  newCredPasswordType,
  setNewCredPasswordType,
  newCredPrivateKeyFileName,
  newCredPrivateKeyError,
  ppkInputRef,
  handlePpkUploadClick,
  handlePpkFileChange,
  newCredExecPasswordEnabled,
  setNewCredExecPasswordEnabled,
  newCredShowExecPassword,
  setNewCredShowExecPassword,
  newCredExecPasswordText,
  setNewCredExecPasswordText,
  newCredAutoAssign,
  setNewCredAutoAssign,
  newCredSave,
  setNewCredSave,
  newCredName,
  setNewCredName,
  fieldSx,
}) => {
  const centeredStyle = { textAlign: 'left', paddingTop: 14, paddingBottom: 14 };

  // When `useLabels` is true, render a floating label (like On‑Prem flows).
  // Otherwise use a placeholder so text stays inside the field.
  const pickFieldProps = (label: string) =>
    useLabels
      ? ({ label, autoComplete: "new-password" } as any)
      : ({ placeholder: label, autoComplete: "new-password", inputProps: { style: centeredStyle, autoComplete: "new-password" } } as any);

  return (
    <div className="nsd-credential-card">
      <div className="nsd-credential-card-header">
        <div className="nsd-credential-card-title">Create New Credential</div>
      </div>
      <div className="nsd-credential-card-body">
        {isAwsScan ? (
          <div className="nsd-field-row">
            <TextField
              {...pickFieldProps("Access Key")}
              value={newCredUsername}
              onChange={(e) => setNewCredUsername(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={fieldSx}
            />
            <TextField
              {...pickFieldProps("Access ID")}
              value={newCredSecret}
              onChange={(e) => setNewCredSecret(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={fieldSx}
            />
          </div>
        ) : (
          <>
            <TextField
              {...pickFieldProps("Username")}
              value={newCredUsername}
              onChange={(e) => setNewCredUsername(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={fieldSx}
            />

            <div>
              <Typography sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}>
                Password Type
              </Typography>
              <FormControl component="fieldset" size="small">
                <RadioGroup
                  row
                  value={newCredPasswordType}
                  onChange={(e) => setNewCredPasswordType(e.target.value as any)}
                >
                  <FormControlLabel
                    value="password"
                    control={<Radio size="small" />}
                    label={
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        Password
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="private_key"
                    control={<Radio size="small" />}
                    label={
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        Private Key
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </div>

            {newCredPasswordType === "private_key" && (
              <>
                <div className="nsd-pem-upload-box">
                  <input
                    type="file"
                    accept=".pem"
                    ref={ppkInputRef}
                    onChange={handlePpkFileChange}
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="contained"
                    onClick={handlePpkUploadClick}
                    sx={{
                      backgroundColor: "#3A2AdA",
                      color: "white",
                      "&:hover": { backgroundColor: "#3225b7" },
                    }}
                  >
                    Upload PEM File
                  </Button>
                  {newCredPrivateKeyFileName && (
                    <Typography variant="body2" sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                      Selected: {newCredPrivateKeyFileName}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                    If a PPK file is available, it should be converted into a PEM file
                  </Typography>
                  {newCredPrivateKeyError && (
                    <Typography variant="body2" color="error" sx={{ mt: 1, fontSize: 12 }}>
                      {newCredPrivateKeyError}
                    </Typography>
                  )}
                </div>

                <FormControlLabel
                  control={
                    <IOSSwitch
                      checked={newCredExecPasswordEnabled}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCredExecPasswordEnabled(e.target.checked)}
                      sx={{ mr: 2 }}
                    />
                  }
                  label={<Typography sx={{ fontSize: 15, fontWeight: 500, lineHeight: "16px" }}>Exec Password</Typography>}
                  labelPlacement="start"
                  sx={{ justifyContent: "space-between", width: "100%", mb: 1 }}
                />

                {newCredExecPasswordEnabled && (
                  <TextField
                    {...pickFieldProps("Exec Password")}
                    type={newCredShowExecPassword ? "text" : "password"}
                    value={newCredExecPasswordText}
                    onChange={(e) => setNewCredExecPasswordText(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={fieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {newCredShowExecPassword ? (
                            <Visibility onClick={() => setNewCredShowExecPassword(!newCredShowExecPassword)} sx={{ cursor: "pointer" }} />
                          ) : (
                            <VisibilityOff onClick={() => setNewCredShowExecPassword(!newCredShowExecPassword)} sx={{ cursor: "pointer" }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </>
            )}

            {newCredPasswordType === "password" && (
              <>
                <TextField
                  {...pickFieldProps("Password")}
                  type={newCredShowPassword ? "text" : "password"}
                  value={newCredSecret}
                  onChange={(e) => setNewCredSecret(e.target.value)}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {newCredShowPassword ? (
                          <Visibility onClick={() => setNewCredShowPassword(!newCredShowPassword)} sx={{ cursor: "pointer" }} />
                        ) : (
                          <VisibilityOff onClick={() => setNewCredShowPassword(!newCredShowPassword)} sx={{ cursor: "pointer" }} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                <div className="nsd-credential-toggle-column">
                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={newCredExecPasswordEnabled}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const checked = e.target.checked;
                          setNewCredExecPasswordEnabled(checked);
                          if (checked) setNewCredAutoAssign(false);
                        }}
                        sx={{ mr: 2 }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 15, fontWeight: 500, lineHeight: "16px" }}>Exec Password</Typography>}
                    labelPlacement="start"
                    sx={{ justifyContent: "space-between", width: "100%", mb: 1 }}
                  />

                  <Typography sx={{ fontSize: 15, fontWeight: 500, lineHeight: "16px", ml: 2, mb: 1 }}>Auto Assign</Typography>

                  <FormControlLabel
                    control={
                      <IOSSwitch
                        checked={newCredAutoAssign}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const checked = e.target.checked;
                          setNewCredAutoAssign(checked);
                          if (checked) {
                            setNewCredExecPasswordEnabled(false);
                            setNewCredExecPasswordText("");
                          }
                        }}
                        sx={{ mr: 2 }}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 400, fontSize: 12, maxWidth: 350, lineHeight: "15px" }}>
                        This will auto-assign Login Password as Exec Password when missing.
                      </Typography>
                    }
                    labelPlacement="start"
                    sx={{ mb: 1, justifyContent: "space-between", width: "100%" }}
                  />
                </div>

                {newCredExecPasswordEnabled && (
                  <TextField
                    {...pickFieldProps("Exec Password")}
                    type={newCredShowExecPassword ? "text" : "password"}
                    value={newCredExecPasswordText}
                    onChange={(e) => setNewCredExecPasswordText(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={fieldSx}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {newCredShowExecPassword ? (
                            <Visibility onClick={() => setNewCredShowExecPassword(!newCredShowExecPassword)} sx={{ cursor: "pointer" }} />
                          ) : (
                            <VisibilityOff onClick={() => setNewCredShowExecPassword(!newCredShowExecPassword)} sx={{ cursor: "pointer" }} />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              </>
            )}

            <FormControlLabel
              control={<Checkbox checked={newCredSave} onChange={(e) => setNewCredSave(e.target.checked)} sx={{ color: "#3a2ada", "&.Mui-checked": { color: "#3a2ada" } }} />}
              label="Save Credential"
            />

            <TextField
              {...pickFieldProps("Credential Name")}
              value={newCredName}
              onChange={(e) => setNewCredName(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={fieldSx}
              required
            />
          </>
        )}
      </div>
    </div>
  );
};

export default CredentialCreator;
